import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, TextField} from '@mui/material';
import useStyles from '../../../../styles/global-mui.js';
import Filters from '../../../components/Filters.js';
import { checkExistingTeam, exportTeams, fetchTeams } from '../../../../api/index.js';
import Pagination from '../../../../dsasComponents/pagination/Pagination.js';
import { useLocation, useNavigate } from 'react-router-dom';
import TeamCard from './TeamCard.js';
import { getUserProvider } from '../../../../actions/auth.js';
import { discipline_all } from '../../../../bdd/bddSTUB.js';
import TeamCheckModal from '../create/TeamCheckModal.js';
import SelectGroupProvider from '../../../../dsasComponents/SelectGroupProvider.js';
import { Can } from '../../../../permissions/Can.js';
import { PERMISSIONS } from '../../../../permissions/permissionKeys.js';
import DsasButton from '../../../components/DsasButton/DsasButton.js';
import { faHourglassHalf, faShieldHalved, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { usePermissions } from '../../../../permissions/usePermissions.js';

const initialTeam = {
  name : "",
  discipline : "",
  provider : getUserProvider()
}


const GetTeams = () => {

  const navigate = useNavigate(); // Utilisation du hook useNavigate pour la redirection
  const location = useLocation();

  const [teams, setTeams] = useState();
  const [loading, setLoading] = useState(false);

  const [teamData, setTeamData] = useState(initialTeam);
  const [existingTeams, setExistingTeams] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCheckComplete, setIsCheckComplete] = useState(false); // Gérer l'étape de vérification

  const [csvFilters, setCSVFilters] = useState({})

  const { getPermission } = usePermissions();
  const providerPermission = getPermission(PERMISSIONS.FILTER_PROVIDER);

  const [exporting, setExporting] = useState(false);

  // Configuration des filtres utilisés par la page GetTeams
  const filtersConfig = [
    { key: 'provider', name: 'provider', fullAccess:providerPermission }, // Le filtre 'provider' sera utilisé avec la clé 'provider'
    { key: 'discipline', name: 'discipline' }, // Le filtre 'discipline'
    { key: 'season', name: 'season' }, // Le filtre 'season'
    { key: 'competition', name: 'competition' }, // Le filtre 'competition'
  ];

  const searchParams = new URLSearchParams(location.search);
  const searchMode = searchParams.get('searchMode') || 'filters';

  const isValidExportTeams = () => {
    return searchMode !== 'simple' && csvFilters?.competition;
  };

  // Synchronize URL parameters with state
  useEffect(() => {
    if (searchMode === 'filters') {
      // Reset state for simple search
      setTeamData(initialTeam);
    }

    setTeams(undefined)
  }, [searchMode]);

  useEffect(() => {
    if (searchMode === 'simple') {
      // Populate state from URL parameters
      const name = searchParams.get('name') || '';
      const discipline = searchParams.get('discipline') || '';
      const provider = searchParams.get('provider') || getUserProvider();
      setTeamData({ name, discipline, provider });
    }
  }, [location.search, searchMode]);

  useEffect(() => {
    if(!isValidCheckExistingTeam())
      return
    
    handleCheckExistingTeam()
  }, [teamData])

  // Gestion de l'application des filtres
  const handleApplyFilters = async (filters) => {
    setLoading(true);
    try {
      const response = await fetchTeams(filters); // Appel à l'API avec les filtres appliqués

      setTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTeamData((prev) => ({ ...prev, [name]: value }));

    // Update URL with the new parameter
    const params = new URLSearchParams(location.search);
    params.set(name, value);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleCheckExistingTeam = async () => {

    setTeams([])

    if(!isValidCheckExistingTeam())
      return

    const { name, discipline } = teamData;
    const results = await checkExistingTeam({ name, discipline });

    const similarTeams = results.data
    
    if(!similarTeams.length){
      setIsCheckComplete(true);
      setIsModalOpen(false);
    }else{
      // On affiche les joueurs du meme provider
      const sameProviderTeams = similarTeams.filter(p => p.provider === teamData.provider)

      const otherProviderTeams = similarTeams.filter(p => p.provider !== teamData.provider)

      if(sameProviderTeams.length){
        setTeams(sameProviderTeams)
      }else if(otherProviderTeams.length){
        setExistingTeams(otherProviderTeams);
        setIsModalOpen(true); // Ouvrir la modal une fois que les résultats sont prêts
      }
    }
  }; 

  const handleSearchModeChange = (mode) => {
    const params = new URLSearchParams();
    params.set('searchMode', mode);
    navigate(`?${params.toString()}`);

    setTeams([])
  };
  
  const isValidCheckExistingTeam = () => {
    return searchMode === 'simple' ? teamData.name && teamData.name!=='' && teamData.discipline : teamData.discipline && teamData.competitions;
  }

  const handleSelectOtherTeam = (team) => {
    // Remplir les champs avec les informations du joueur sélectionné

    const copyTeam = {
      name: team.name,
      fullName: team.fullName,
      discipline: team.discipline,
      color : team.color,
      awayColor : team.awayColor,
      textColor : team.textColor,

      logoName: team.logoName,
      logoUrl: team.logoUrl,

      stadiumName: team.stadiumName,
      stadiumCapacity: team.stadiumCapacity,
      photoStadiumName: team.photoStadiumName,
      photoStadiumUrl: team.photoStadiumUrl,

      country: team.country,
      teamCreation: team.teamCreation,
      website: team.website,
      president: team.president,
      coaches: team.coaches,
      subscription: team.subscription,

      thirdPartyId: team.thirdPartyId,
      thirdPartyEntityId: team.thirdPartyEntityId,
      provider : teamData.provider,
      competitions : [],
      competitionsPenality : [],
    }

    navigate(`/sports/teams/create`, { state: { copyTeam } }); // Naviguer avec les données de l'equipe

    setIsCheckComplete(true); // L'utilisateur a passé la vérification
    setIsModalOpen(false);
  };

  const handleConfirmNone = () => {
    setIsCheckComplete(true); // Marquer que l'utilisateur a bien vérifié
    setIsModalOpen(false);

    navigate(`/sports/teams/create`, { state: { copyTeam : teamData } }); // Naviguer avec les données de l'equipe
  };

  const handleCreateClick = () => {
    navigate(`/sports/teams/create`); // Redirection vers la page de creation
  };

  const sanitizeForFilename = (value = '') =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

const buildTeamsFilename = (filters) => {
  const provider = sanitizeForFilename(filters.provider || 'provider');
  const discipline = sanitizeForFilename(filters.discipline || 'discipline');
  const season = sanitizeForFilename(filters.season || 'season');
  const competition = sanitizeForFilename(filters.competitionName || filters.competition || 'competition');
  return `teams_${provider}_${discipline}_${season}_${competition}.csv`;
};

const handleExportCSV = async () => {
  setExporting(true);
  try {
    // garde les champs UI (competitionName…) pour le filename, mais ne les envoie pas au backend
    const base = { ...csvFilters };
    const { competitionName, providerName, ...payload } = base;

    const response = await exportTeams(payload);

    if (response.status !== 200) throw new Error('Erreur lors du téléchargement du CSV');

    const csvContent = await response.data;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);

    const filename = buildTeamsFilename(base);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    setExporting(false);
  }
};

    const removeTeamFromList = (id) => {
      setTeams(s => s.filter(t => t._id !== id))
    }

    const classes = useStyles();

    let exportResult = searchMode !== 'simple';
    let showExport = true;
    let showPicture = true;

    //TODO opti evol : ajouter la notion de mandatory dans le filtersConfig
    const mantatoryFieldsInfo = searchMode === 'simple' ? 'Nom, Discipline' : 'Discipline, Compétition';

  return (
    <Box>

      {/*PAGE TITLE*/}
      <Box className='centered-medium-wrapper'>
        <h1 className='page-title'>Gestion des équipes</h1>
      </Box>
      <Box className='medium-separator'></Box>
      
      {/*CREATE BUTTON SECTION*/}
      <Box className='centered-medium-wrapper'>
        <Can permission={PERMISSIONS.SPORT_TEAM_HOME_BUTTON_CREATE}>
          <DsasButton label="Créer une équipe" icon={faShieldHalved} onClick={handleCreateClick} variant = 'secondary' />
        </Can>
      </Box>
      <Box className='medium-separator'></Box>

       {/*SEARCH SECTION*/}
      <Box className='centered-medium-wrapper'>
        {/*SEARCH BY NAME BUTTON*/}
        <DsasButton label="Rechercher par nom" onClick={() => handleSearchModeChange('simple')} variant = 'primarysub' focused = {searchMode === 'simple'} />
        {/*SEARCH BY COMEPTITION BUTTON*/}
        <DsasButton label="Rechercher par competition" onClick={() => handleSearchModeChange('filters')} variant = 'primarysub' focused = {searchMode !== 'simple'} />
      </Box>

      {/*SEARCH FILTERS*/}
      <Box className='padding-wrapper'>

      {searchMode === 'simple' && (
          <>

          <Grid container spacing={1.5} sx={{alignItems : 'flex-end'}}>

            {providerPermission?.allowed && (
              <Grid item xs={12} sm={12} md>
                <SelectGroupProvider
                  sx={{ flex: '1 1 150px', minWidth: '150px' }}
                  value={teamData.provider} onChange={handleChange} className={classes.customInput}/>
              </Grid>
            )}
          
            <Grid item xs={12} sm={12} md>
               {/* Nom */}
                <TextField 
                  className={classes.customInput}
                  name="name" 
                  label="Nom" 
                  error={!teamData.name} 
                  fullWidth 
                  value={teamData.name} 
                  onChange={handleChange} />
            </Grid>

            <Grid item xs={12} sm={12} md>
              {/* Discipline */}
              <FormControl fullWidth className={classes.customInput} error={!teamData.discipline} sx={{ flex: '1 1 150px', minWidth: '150px' }}>
              <InputLabel>Discipline</InputLabel>
              <Select name="discipline" value={teamData.discipline}   onChange={handleChange} label={"Discipline"}>
                  {discipline_all.map((discipline) => (
                  <MenuItem key={discipline._id} value={discipline._id}>
                      {discipline.name}
                  </MenuItem>
                  ))}
              </Select>
              </FormControl> 
            </Grid>
        </Grid>

          {/* Modal de vérification des doublons */}
          <TeamCheckModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          results={existingTeams}
          onSelectOtherTeam={handleSelectOtherTeam}
          onConfirmNone={handleConfirmNone}
          actualProvider={teamData.provider}
          />
        </>
      )}

{searchMode === 'filters' && (
  <>
    {/* <Typography variant='subtitle2' mb={2}>Rechercher par compétition</Typography> */}

    {/* Composant Filters mis à jour avec les nouveaux filtres */}
    <Filters filtersConfig={filtersConfig} onApplyFilters={handleApplyFilters} setCSVFilters={setCSVFilters} className={classes.customInput} onReset={() => setTeams([])}/>
  </>
)}

      {loading ? (
        <CircularProgress />
      ) : !teams ? <Box className='left-medium-wrapper'>{`Veuillez saisir les filtres obligatoires (${mantatoryFieldsInfo}).`}</Box> : teams.length ? (
        <>
          {searchMode === 'filters' && isValidExportTeams() && (
              <Can permission={PERMISSIONS.SPORT_TEAM_HOME_BUTTON_EXPORTCSV}>
                <Box className="left-medium-wrapper">
                  <DsasButton
                    label="Exporter les équipes en CSV"
                    icon={!exporting ? faUserPlus : faHourglassHalf}
                    onClick={handleExportCSV}
                    variant="secondary"
                    disabled={exporting}
                  />
                </Box>
              </Can>
            )}
          <Box className='medium-separator medium-padding'></Box>
              {/*resultats*/}
              <Box className='padding-wrapper'>
                  <Pagination items={teams} type='Equipes' className={classes.customTablePagination}>
                    {teams.map((team, index) => (
                      // <Box
                      //   key={team._id}
                      //   sx={{
                      //     flex: '1 1 300px', // La carte occupe un minimum de 300px et s'étire avec l'espace disponible
                      //   }}
                      // >
                        <TeamCard team={team} removeTeamFromList={removeTeamFromList} className={classes.customCard} 
                        showExport={showExport}
                        showPicture={showPicture}/>
                      // </Box>
                    ))}
                  </Pagination>
              </Box>
        </>
      ) : (searchMode === 'simple' && isValidCheckExistingTeam()) ? (
        <>
        {teams.length===0 && <Box className='left-medium-wrapper '>Aucune équipe trouvée.</Box>}
        <Box sx={{textAlign : 'center', m : 2}}>
          <Can permission={PERMISSIONS.SPORT_TEAM_HOME_BUTTON_CREATE}>
          <Button size="large" color="primary" variant="contained" onClick={() => handleSelectOtherTeam(teamData)}>
            Créer
          </Button>
          </Can>
        </Box>
        </>
      ) : (
        <>
        { isValidCheckExistingTeam() && teams.length===0 && <Box className='left-medium-wrapper '>Aucune équipe trouvée.</Box>}
        { !isValidCheckExistingTeam() && <Box className='left-medium-wrapper '>{`Veuillez saisir les filtres obligatoires (${mantatoryFieldsInfo}).`}</Box>}
      </>
      )}
      </Box>
    </Box>
  );
};

export default GetTeams;
